import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Text from '~go/web/src/js/components/common/text/text';
import ScrollShadowWrapperX from '~go/web/src/js/components/common/scroll_shadow_wrapper/scroll_shadow_wrapper_x';
import useBS5Breakpoints from '~go/web/src/js/hooks/use_bs5_breakpoints';
import getSSRData from '~go/web/src/js/utils/get_ssr_data';
import { motion } from 'motion/react';
import { clsx } from '~go/web/src/js/utils/clsx/clsx';
import section8Content from './section_8_content.json';

const { httpHost, rootPath } = getSSRData('ssr-data');
const { section8Items } = section8Content;

function Section8MainContent() {
  const [activeItem, setActiveItem] = useState(section8Items[0]);
  const scrollRef = useRef(null);

  return (
    <div>
      {/* Nav tabs */}
      <div className="d-flex justify-content-center">
        <ScrollShadowWrapperX
          ref={scrollRef}
          color1="var(--really-light-gray)"
          color2="var(--really-light-gray)"
          innerContainerProps={{
            className: 'd-flex flex-nowrap mt-2 mb-1',
            style: { gap: 2 },
          }}
        >
          {section8Items.map(item => (
            <NavItem key={item.tab} item={item} isActive={activeItem.tab === item.tab} setActiveItem={setActiveItem} />
          ))}
        </ScrollShadowWrapperX>
      </div>

      {/* Active tab content */}
      <div className="mt-5">
        <TabPane item={activeItem} />
      </div>
    </div>
  );
}

export default Section8MainContent;

function NavItem({ item, isActive, setActiveItem }) {
  return (
    <Text
      component="div"
      variant="marketing-xs"
      weight="bold"
      color={isActive ? 'default-text' : 'tertiary-text'}
      className={clsx(
        'd-flex align-items-center rounded-pill cursor-pointer py-1 px-2',
        isActive && 'bg-gray',
        !isActive && 'hover-bg-light-gray'
      )}
      data-toggle="pill"
      role="tab"
      onClick={() => setActiveItem(item)}
    >
      {item.tab}
    </Text>
  );
}

NavItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  setActiveItem: PropTypes.func.isRequired,
};

function TabPane({ item }) {
  const isMdBreakpoint = useBS5Breakpoints({ breakpoint: 'md' });
  let gridTemplateColumns = '1fr';
  if (isMdBreakpoint) gridTemplateColumns = '1fr 1fr';

  const baseDelay = 0.1;

  /**
   * When the tab changes, the tab pane will animate, and the problem is that there is a very short
   * moment where the height becomes 0, which causes a visual flicker of the next section's content
   * jumping up and down very quickly.
   *
   * To prevent this, we make this dummy clone take the space, while the actual content is absolutely positioned.
   * This protects the height from shrinking.
   */
  const dummyCloneJustToKeepSpace = (
    <div
      className="d-grid"
      style={{ gridTemplateColumns, gap: 30, opacity: 0, userSelect: 'none', pointerEvents: 'none' }}
    >
      {/* Text parts */}
      <div className="d-flex flex-column justify-content-center">
        <Text component="h3" variant="marketing-h3">
          {item.title}
        </Text>
        <Text component="p" variant="marketing-body" color="tertiary-text" className="mt-1">
          {item.description}
        </Text>
        {item.ctaButtonLink && (
          <a
            href={`https://${httpHost}${rootPath}${item.ctaButtonLink}`}
            className="btn btn-primary rounded mt-2 w-fit"
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.ctaButtonText}
          </a>
        )}
      </div>
      {/* Image parts */}
      <div className="d-flex flex-column gap-1 bg-light-gray rounded-lg p-3 p-md-5">
        <img src={`${item.imageBaseFolder}/1.webp`} alt={item.imageAlt} className="w-100" />
        <img src={`${item.imageBaseFolder}/2.webp`} alt={item.imageAlt} className="w-100" />
        <img src={`${item.imageBaseFolder}/3.webp`} alt={item.imageAlt} className="w-100" />
      </div>
    </div>
  );

  return (
    <div className="position-relative">
      {/* Dummy content to protect height */}
      {dummyCloneJustToKeepSpace}

      {/* Actual content */}
      <motion.div
        key={item.tab}
        className="d-grid position-absolute"
        style={{
          gridTemplateColumns,
          gap: 30,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {/* Text parts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="d-flex flex-column justify-content-center"
        >
          <Text component="h3" variant="marketing-h3">
            {item.title}
          </Text>
          <Text component="p" variant="marketing-body" color="tertiary-text" className="mt-1">
            {item.description}
          </Text>
          {item.ctaButtonLink && (
            <a
              href={`https://${httpHost}${rootPath}${item.ctaButtonLink}`}
              className="btn btn-primary rounded mt-2 w-fit"
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.ctaButtonText}
            </a>
          )}
        </motion.div>

        {/* Image parts */}
        <motion.div
          className="d-flex flex-column gap-1 bg-light-gray rounded-lg p-3 p-md-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: baseDelay * 1, duration: 0.25, ease: 'easeInOut' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: baseDelay * 2, duration: 0.25, ease: 'easeInOut' }}
          >
            <img src={`${item.imageBaseFolder}/1.webp`} alt={item.imageAlt} className="w-100" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: baseDelay * 3, duration: 0.25, ease: 'easeInOut' }}
          >
            <img src={`${item.imageBaseFolder}/2.webp`} alt={item.imageAlt} className="w-100" />
          </motion.div>
          <motion.div
            className="mr-n3 mr-md-n5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: baseDelay * 4, duration: 0.25, ease: 'easeInOut' }}
          >
            <img src={`${item.imageBaseFolder}/3.webp`} alt={item.imageAlt} className="w-100" />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

TabPane.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
};
